import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail, Send, Download, LogOut, Lock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'sp_admin_pwd';

export default function AdminWaitlist() {
  const [password, setPassword] = useState(() => sessionStorage.getItem(STORAGE_KEY) || '');
  const [pwdInput, setPwdInput] = useState('');
  const [authed, setAuthed] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [listLoading, setListLoading] = useState(false);

  // Launch email form
  const [playUrl, setPlayUrl] = useState('');
  const [appUrl, setAppUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [confirming, setConfirming] = useState(false);

  // Try to auto-authenticate using stored password
  useEffect(() => {
    let cancelled = false;
    async function tryAuto() {
      if (!password) { setAuthLoading(false); return; }
      try {
        const res = await fetch(`${API}/admin/auth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password }),
        });
        if (cancelled) return;
        if (res.ok) {
          setAuthed(true);
          await loadList(password);
        } else {
          sessionStorage.removeItem(STORAGE_KEY);
          setPassword('');
        }
      } catch {
        // network error — keep on login screen
      } finally {
        if (!cancelled) setAuthLoading(false);
      }
    }
    tryAuto();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadList = async (pwd) => {
    setListLoading(true);
    try {
      const res = await fetch(`${API}/admin/waitlist/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwd }),
      });
      if (!res.ok) throw new Error('list_failed');
      const data = await res.json();
      setEntries(data.entries || []);
      setStats(data.stats || null);
    } catch {
      toast.error('Erreur de chargement de la liste');
    } finally {
      setListLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const res = await fetch(`${API}/admin/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwdInput }),
      });
      if (!res.ok) {
        setAuthError('Mot de passe invalide');
        return;
      }
      sessionStorage.setItem(STORAGE_KEY, pwdInput);
      setPassword(pwdInput);
      setAuthed(true);
      await loadList(pwdInput);
    } catch {
      setAuthError('Erreur réseau');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPassword('');
    setPwdInput('');
    setAuthed(false);
    setEntries([]);
    setStats(null);
  };

  const exportCSV = () => {
    if (!entries.length) {
      toast.error('Aucune entrée à exporter');
      return;
    }
    const rows = [['email', 'lang', 'source', 'platform', 'created_at', 'launch_email_sent_at']];
    entries.forEach((e) => {
      rows.push([
        e.email || '',
        e.lang || '',
        e.source || '',
        e.platform || '',
        e.created_at || '',
        e.launch_email_sent_at || '',
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `waitlist_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`✓ ${entries.length} entrées exportées`);
  };

  const sendLaunchEmail = async () => {
    if (!playUrl && !appUrl) {
      toast.error('Fournis au moins un lien (Play Store ou App Store)');
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const res = await fetch(`${API}/admin/waitlist/send-launch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          play_store_url: playUrl.trim(),
          app_store_url: appUrl.trim(),
          dry_run: false,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Erreur d\'envoi');
        return;
      }
      setSendResult(data);
      toast.success(`✓ ${data.sent} courriels envoyés`);
      // Refresh the list to reflect launch_email_sent_at
      await loadList(password);
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setSending(false);
      setConfirming(false);
    }
  };

  // ============ LOGIN SCREEN ============
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAF7F2' }}>
        <Loader2 className="animate-spin text-[#7A1F2B]" size={36} />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#FAF7F2' }}>
        <div className="w-full max-w-sm bg-white rounded-2xl p-6 sm:p-8 border-2" style={{ borderColor: '#E5DCC9' }}>
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl mx-auto mb-4" style={{ background: '#7A1F2B' }}>
            <Lock className="text-white" size={24} />
          </div>
          <h1 className="font-heading text-2xl font-black text-center mb-2" style={{ color: '#7A1F2B' }}>
            Administration
          </h1>
          <p className="text-sm text-gray-500 text-center mb-6">SoumissionPro — Accès restreint</p>
          <form onSubmit={handleLogin}>
            <input
              data-testid="admin-password-input"
              type="password"
              autoFocus
              required
              value={pwdInput}
              onChange={(e) => setPwdInput(e.target.value)}
              className="field w-full mb-3"
              placeholder="Mot de passe administrateur"
            />
            {authError && (
              <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{authError}</span>
              </div>
            )}
            <button
              data-testid="admin-login-btn"
              type="submit"
              className="btn-primary w-full"
            >
              Se connecter
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ============ DASHBOARD ============
  return (
    <div className="min-h-screen" style={{ background: '#FAF7F2' }}>
      <header className="px-4 sm:px-8 py-4 flex items-center justify-between border-b bg-white" style={{ borderColor: '#E5DCC9' }}>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: '#C9A961' }}>
            ADMINISTRATION
          </div>
          <h1 className="font-heading text-xl sm:text-2xl font-black" style={{ color: '#7A1F2B' }}>
            Liste d'attente — App Store + Google Play
          </h1>
        </div>
        <button
          data-testid="admin-logout-btn"
          onClick={handleLogout}
          className="inline-flex items-center gap-2 px-3 h-10 rounded-lg border-2 text-sm font-bold"
          style={{ borderColor: '#E5DCC9', color: '#7A1F2B' }}
        >
          <LogOut size={14} /> Déconnexion
        </button>
      </header>

      <main className="max-w-5xl mx-auto p-4 sm:p-8 space-y-6">
        {/* Nav sub-tabs */}
        <nav className="flex gap-2 text-sm">
          <Link to="/admin/dashboard" className="px-4 h-10 inline-flex items-center rounded-xl font-bold border-2" style={{ borderColor: '#E5DCC9', color: '#7A1F2B' }}>
            Tableau de bord
          </Link>
          <Link to="/admin/waitlist" className="px-4 h-10 inline-flex items-center rounded-xl font-bold text-white" style={{ background: '#7A1F2B' }}>
            Liste d'attente
          </Link>
        </nav>

        {/* Stats */}
        {stats && (
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Total inscrits" value={stats.total} accent="#7A1F2B" testId="stat-total" />
            <StatCard label="Français" value={stats.by_lang?.fr || 0} accent="#C9A961" testId="stat-fr" />
            <StatCard label="English" value={stats.by_lang?.en || 0} accent="#C9A961" testId="stat-en" />
            <StatCard
              label="Source pricing"
              value={stats.by_source?.pricing || 0}
              accent="#7A1F2B"
              testId="stat-pricing"
            />
          </section>
        )}

        {/* Launch email form */}
        <section className="bg-white rounded-2xl p-6 border-2" style={{ borderColor: '#E5DCC9' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#7A1F2B' }}>
              <Mail className="text-white" size={20} />
            </div>
            <div>
              <h2 className="font-heading font-black text-lg" style={{ color: '#7A1F2B' }}>
                Courriel de lancement officiel
              </h2>
              <p className="text-xs text-gray-500">
                Envoyer à TOUS les inscrits qui n'ont pas encore reçu l'annonce.
              </p>
            </div>
          </div>

          <label className="block text-sm font-semibold mb-1.5">Lien Google Play (recommandé)</label>
          <input
            data-testid="play-store-url"
            type="url"
            value={playUrl}
            onChange={(e) => setPlayUrl(e.target.value)}
            placeholder="https://play.google.com/store/apps/details?id=..."
            className="field w-full mb-3"
          />

          <label className="block text-sm font-semibold mb-1.5">Lien App Store (recommandé)</label>
          <input
            data-testid="app-store-url"
            type="url"
            value={appUrl}
            onChange={(e) => setAppUrl(e.target.value)}
            placeholder="https://apps.apple.com/ca/app/..."
            className="field w-full mb-4"
          />

          <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded text-xs text-amber-900 mb-4">
            <b>⚠️ Action irréversible.</b> Le courriel sera envoyé à <b>{entries.filter((e) => !e.launch_email_sent_at).length}</b> destinataires non encore avertis. Les inscrits qui ont déjà reçu l'email seront ignorés automatiquement.
          </div>

          {!confirming ? (
            <button
              data-testid="send-launch-prepare"
              onClick={() => setConfirming(true)}
              disabled={!playUrl && !appUrl}
              className="btn-primary w-full gap-2 disabled:opacity-50"
            >
              <Send size={16} /> Préparer l'envoi du courriel de lancement
            </button>
          ) : (
            <div className="space-y-2">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                <b>Confirmer l'envoi à {entries.filter((e) => !e.launch_email_sent_at).length} personnes ?</b>
                <br />Cette action ne peut pas être annulée.
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirming(false)}
                  className="flex-1 h-12 rounded-xl border-2 font-bold text-sm"
                  style={{ borderColor: '#E5DCC9', color: '#7A1F2B' }}
                >
                  Annuler
                </button>
                <button
                  data-testid="send-launch-confirm"
                  onClick={sendLaunchEmail}
                  disabled={sending}
                  className="flex-1 h-12 rounded-xl font-bold text-sm text-white disabled:opacity-60"
                  style={{ background: '#DC2626' }}
                >
                  {sending ? (
                    <span className="inline-flex items-center gap-2 justify-center">
                      <Loader2 className="animate-spin" size={14} /> Envoi…
                    </span>
                  ) : (
                    'Oui, envoyer maintenant'
                  )}
                </button>
              </div>
            </div>
          )}

          {sendResult && (
            <div data-testid="send-result" className="mt-4 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <div className="flex items-start gap-2">
                <CheckCircle2 size={18} className="text-emerald-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900">
                  <b>{sendResult.sent} courriels envoyés.</b>
                  {sendResult.failed > 0 && (
                    <> {sendResult.failed} échecs ({sendResult.failed_emails?.join(', ')}).</>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>

        {/* List + export */}
        <section className="bg-white rounded-2xl border-2 overflow-hidden" style={{ borderColor: '#E5DCC9' }}>
          <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: '#E5DCC9' }}>
            <h2 className="font-heading font-black" style={{ color: '#7A1F2B' }}>
              Inscrits ({entries.length})
            </h2>
            <button
              data-testid="export-csv-btn"
              onClick={exportCSV}
              className="inline-flex items-center gap-2 px-3 h-9 rounded-lg border-2 text-xs font-bold"
              style={{ borderColor: '#E5DCC9', color: '#7A1F2B' }}
            >
              <Download size={14} /> Exporter CSV
            </button>
          </div>

          {listLoading ? (
            <div className="p-8 text-center">
              <Loader2 className="animate-spin text-[#7A1F2B] mx-auto" size={28} />
            </div>
          ) : entries.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              Aucun inscrit pour l'instant.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF7F2] text-[11px] uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-2">Courriel</th>
                    <th className="text-left px-4 py-2">Lang</th>
                    <th className="text-left px-4 py-2">Source</th>
                    <th className="text-left px-4 py-2">Inscrit le</th>
                    <th className="text-left px-4 py-2">Lancement envoyé</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => (
                    <tr key={e.email + i} className="border-t" style={{ borderColor: '#E5DCC9' }}>
                      <td className="px-4 py-2 font-mono text-xs">{e.email}</td>
                      <td className="px-4 py-2">{(e.lang || 'fr').toUpperCase()}</td>
                      <td className="px-4 py-2 text-xs text-gray-600">{e.source || '—'}</td>
                      <td className="px-4 py-2 text-xs text-gray-600">
                        {e.created_at ? new Date(e.created_at).toLocaleDateString('fr-CA') : '—'}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {e.launch_email_sent_at ? (
                          <span className="text-emerald-700">✓ {new Date(e.launch_email_sent_at).toLocaleDateString('fr-CA')}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function StatCard({ label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl p-4 bg-white border-2"
      style={{ borderColor: '#E5DCC9' }}
    >
      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
        {label}
      </div>
      <div className="text-3xl font-black mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}
